import { ConfirmModal } from '@app/components/modals/ConfirmModal'
import { useMe } from '@app/lib'
import {
  type SugarMe,
  type SugarOption,
  type SugarOptionName,
  type SugarPeriod,
  type SugarProgramYear
} from '@app/types'
import { useLocalStorage } from '@mantine/hooks'
import { filter, find, first, last } from 'lodash'
import {
  createContext,
  type FC,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'

export const SugarContext = createContext<
  Partial<SugarMe> & {
    attendancePeriodId: string | null | undefined
    attendancePeriods: SugarPeriod[]
    attendanceProgramYearId: string | null | undefined
    attendanceProgramYears: SugarProgramYear[]
    clearPreferences: () => void
    getOptionByValue: (option: SugarOptionName, value: string) => SugarOption | undefined
    getOptionLabelByValue: (option: SugarOptionName, value: string) => string | undefined
    isSelectionRestored: boolean
    lettersProgramYears: SugarProgramYear[]
    requestRosterProgramYearChange: (programYearId: string) => void
    // onApplied runs only if the change actually goes through, so a caller's
    // side effects cannot outlive a switch the provider cancelled.
    requestSiteChange: (siteId: string, onApplied?: () => void) => void
    rosterProgramYearId: string | null | undefined
    rosterProgramYears: SugarProgramYear[]
    setAttendanceProgramYearId: (programYearId: string) => void
    setAttendancePeriodId: (periodId: string) => void
    setHasUnsavedChanges: (hasUnsavedChanges: boolean) => void
    setRosterProgramYearId: (programYearId: string) => void
    setSiteId: (siteId: string) => void
    siteId: string | null | undefined
  }
>({
  attendancePeriodId: undefined,
  attendancePeriods: [],
  attendanceProgramYearId: undefined,
  attendanceProgramYears: [],
  clearPreferences: () => {},
  getOptionByValue: () => undefined,
  getOptionLabelByValue: () => undefined,
  isSelectionRestored: false,
  lettersProgramYears: [],
  requestRosterProgramYearChange: () => {},
  requestSiteChange: () => {},
  rosterProgramYearId: undefined,
  rosterProgramYears: [],
  setAttendanceProgramYearId: () => {},
  setAttendancePeriodId: () => {},
  setHasUnsavedChanges: () => {},
  setRosterProgramYearId: () => {},
  setSiteId: () => {},
  siteId: undefined
})

export const useSugarContext = () => useContext(SugarContext)

export interface SugarProviderProps {
  children: ReactNode
}

export const SugarProvider: FC<SugarProviderProps> = ({ children }) => {
  const { data: me } = useMe()
  const sites = useMemo(() => me?.sites ?? [], [me?.sites])
  const keyPrefix = `dpp:${me?.profile?.id ?? 'anon'}:`

  const [storedSiteId, setStoredSiteId] = useLocalStorage<string | null>({
    key: `${keyPrefix}siteId`,
    defaultValue: null,
    getInitialValueInEffect: true
  })
  const site = useMemo(() => {
    const found = storedSiteId ? find(sites, ['id', storedSiteId]) : undefined
    return found ?? sites[0]
  }, [sites, storedSiteId])

  const attendanceProgramYears = useMemo(() => filter(site?.programYears, 'attendance'), [site?.programYears])
  const [storedAttendanceProgramYearId, setStoredAttendanceProgramYearId] = useLocalStorage<string | null>({
    key: `${keyPrefix}attendanceProgramYearId`,
    defaultValue: null,
    getInitialValueInEffect: true
  })
  const attendanceProgramYear = useMemo(() => {
    const found = storedAttendanceProgramYearId
      ? find(attendanceProgramYears, ['id', storedAttendanceProgramYearId])
      : undefined
    return found ?? first(attendanceProgramYears)
  }, [attendanceProgramYears, storedAttendanceProgramYearId])

  const rosterProgramYears = useMemo(() => filter(site?.programYears, 'roster'), [site?.programYears])
  const [storedRosterProgramYearId, setStoredRosterProgramYearId] = useLocalStorage<string | null>({
    key: `${keyPrefix}rosterProgramYearId`,
    defaultValue: null,
    getInitialValueInEffect: true
  })
  const rosterProgramYear = useMemo(() => {
    const found = storedRosterProgramYearId ? find(rosterProgramYears, ['id', storedRosterProgramYearId]) : undefined
    return found ?? first(rosterProgramYears)
  }, [rosterProgramYears, storedRosterProgramYearId])

  const lettersProgramYears = useMemo(() => filter(site?.programYears, 'letters'), [site?.programYears])

  const attendancePeriods = useMemo(() => attendanceProgramYear?.periods ?? [], [attendanceProgramYear?.periods])
  const [storedAttendancePeriodId, setStoredAttendancePeriodId] = useLocalStorage<string | null>({
    key: `${keyPrefix}attendancePeriodId`,
    defaultValue: null,
    getInitialValueInEffect: true
  })
  const attendancePeriod = useMemo(() => {
    const found = storedAttendancePeriodId ? find(attendancePeriods, ['id', storedAttendancePeriodId]) : undefined
    return found ?? last(attendancePeriods)
  }, [attendancePeriods, storedAttendancePeriodId])

  // Declared after every useLocalStorage call above: React runs a component's
  // effects in declaration order, and useLocalStorage (getInitialValueInEffect:
  // true) applies its stored value from a useEffect. Declaring this first would
  // flip it to true before the stored ids landed, defeating the point of the flag.
  // Inlined rather than Mantine's useMounted because this repo is pinned to
  // @mantine/hooks 6, which predates that hook (added in v7) — this is its
  // upstream implementation (useState + an empty-deps useEffect).
  const [isSelectionRestored, setIsSelectionRestored] = useState(false)
  useEffect(() => setIsSelectionRestored(true), [])

  const getOptionByValue = useCallback(
    (option: SugarOptionName, value: string) => find(me?.options[option], { value }),
    [me]
  )
  const getOptionLabelByValue = useCallback(
    (option: SugarOptionName, value: string) => getOptionByValue(option, value)?.label,
    [getOptionByValue]
  )
  const setSiteId = useCallback((siteId: string) => setStoredSiteId(siteId), [setStoredSiteId])
  const setAttendanceProgramYearId = useCallback(
    (programYearId: string) => setStoredAttendanceProgramYearId(programYearId),
    [setStoredAttendanceProgramYearId]
  )
  const setRosterProgramYearId = useCallback(
    (programYearId: string) => setStoredRosterProgramYearId(programYearId),
    [setStoredRosterProgramYearId]
  )
  const setAttendancePeriodId = useCallback(
    (periodId: string) => setStoredAttendancePeriodId(periodId),
    [setStoredAttendancePeriodId]
  )

  const clearPreferences = useCallback(() => {
    if (!me?.profile?.id) return
    const prefix = `dpp:${me.profile.id}:`
    for (let i = window.localStorage.length - 1; i >= 0; i--) {
      const key = window.localStorage.key(i)
      if (key?.startsWith(prefix)) window.localStorage.removeItem(key)
    }
  }, [me?.profile?.id])

  // Screens with an editable form publish their dirty state here so in-page
  // site and program-year switches can prompt before discarding it. Route
  // navigation is covered separately by useBlockNavigation; this is only for
  // changes that never touch the router.
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  // Held in a ref rather than state because it is only ever read inside the
  // confirm handler — storing the function in state would invoke it as a
  // lazy initializer.
  const pendingChange = useRef<(() => void) | null>(null)
  const [isConfirmingChange, setIsConfirmingChange] = useState(false)

  // Stage-and-apply, never call-and-revert: useLocalStorage writes synchronously,
  // so applying first would persist the wrong selection if the tab closed while
  // the modal was open. The pickers keep showing the current value until confirm,
  // which is what lets cancel be a no-op.
  const requestChange = useCallback(
    (apply: () => void) => {
      if (!hasUnsavedChanges) {
        apply()

        return
      }

      pendingChange.current = apply
      setIsConfirmingChange(true)
    },
    [hasUnsavedChanges]
  )

  const requestSiteChange = useCallback(
    (nextSiteId: string, onApplied?: () => void) =>
      requestChange(() => {
        setStoredSiteId(nextSiteId)
        onApplied?.()
      }),
    [requestChange, setStoredSiteId]
  )
  const requestRosterProgramYearChange = useCallback(
    (programYearId: string) => requestChange(() => setStoredRosterProgramYearId(programYearId)),
    [requestChange, setStoredRosterProgramYearId]
  )

  const cancelChange = useCallback(() => {
    pendingChange.current = null
    setIsConfirmingChange(false)
  }, [])
  const confirmChange = useCallback(() => {
    const apply = pendingChange.current

    pendingChange.current = null
    setIsConfirmingChange(false)
    // Deliberately does not clear hasUnsavedChanges. The edits are not gone
    // until the publishing screen is actually reseeded, and that can be several
    // seconds later — or never, if the new selection fails to load. Clearing
    // here would leave the guard off while the edits were still on screen, so a
    // second switch would discard them with no prompt at all. The publisher
    // owns the flag and re-asserts it every render.
    apply?.()
  }, [])

  return (
    <SugarContext.Provider
      value={{
        ...me,
        attendancePeriodId: attendancePeriod?.id,
        attendancePeriods,
        attendanceProgramYearId: attendanceProgramYear?.id,
        attendanceProgramYears,
        clearPreferences,
        getOptionByValue,
        getOptionLabelByValue,
        isSelectionRestored,
        lettersProgramYears,
        requestRosterProgramYearChange,
        requestSiteChange,
        rosterProgramYearId: rosterProgramYear?.id,
        rosterProgramYears,
        setAttendancePeriodId,
        setAttendanceProgramYearId,
        setHasUnsavedChanges,
        setRosterProgramYearId,
        setSiteId,
        siteId: site?.id,
        sites
      }}
    >
      {children}
      <ConfirmModal
        onClose={cancelChange}
        onConfirm={confirmChange}
        opened={isConfirmingChange}
      >
        {/* Deliberately not the route-navigation dialog's "want to leave?": this
            switch keeps the provider on the same page, so the accurate warning
            is that switching discards the edits, not that they are navigating
            away. */}
        You have unsaved changes. Switching site or program year will discard them.
      </ConfirmModal>
    </SugarContext.Provider>
  )
}
